import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {ConsignmentUtils, GoodsItemSummaryWithHouseConsignments} from "../../consignment.utils";
import {
  GetVisit,
  IE3ConsignmentHouseLevel,
  IE3ConsignmentProcess,
  IE3ContainerProcess,
  IE3GetConsignment,
  IE3GetContainerProcess,
  IE3GoodsItem,
  IE3GoodsPlacement,
  IE3SaveConsignment,
  IE3SizeType,
  IE3TransportEquipment,
  Visit
} from "@portbase/bezoekschip-service-typescriptmodels";
import {
  checkValidity,
  formDataSaved,
  openConfirmationModalWithCallback,
  openEditModal,
  sendCommand,
  sendQuery
} from "../../../../common/utils";
import {
  MasterConsignmentDetailsComponent,
  MasterConsignmentDetailsComponentData
} from "../master-consignment-details/master-consignment-details.component";
import {
  HouseConsignmentDetailsComponent,
  HouseConsignmentDetailsComponentData
} from "../house-consignment-details/house-consignment-details.component";
import {ContainerUtils} from "../../../containers/container.utils";
import {PortvisitUtils} from "../../../../refdata/portvisit-utils";
import {ModalConfirmAutofocus, ModalConfirmAutofocusData} from "../../../../common/modal/modal-confirm.component";
import {ConsignmentRules} from "../../consignment.rules";
import {publishHandlerEvent} from "../../../../common/handlers/app-common-utils";
import {HandleQuery} from "../../../../common/handlers/handle-query";
import {Observable, of} from "rxjs";
import {Handler} from "../../../../common/handlers/handler";
import {View} from "../../../../common/handlers/view";
import {GetErrorPointers} from "../../../handlers/consignment-handler";
import {ErrorPointer, ErrorWithPointers} from "../../../../common/errors/errors";
import {map} from "rxjs/operators";
import lodash from "lodash";
import {TabInfo} from "../../../../common/tab-panel/tab-panel.component";

@Component({
    selector: 'app-equipment-consignment-details',
    templateUrl: './equipment-consignment-details.component.html',
    styleUrls: ['./equipment-consignment-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
@Handler()
export class EquipmentConsignmentDetailsComponent extends View implements OnInit {
  utils = ConsignmentUtils;
  portVisitUtils = PortvisitUtils;

  consignmentProcess: IE3ConsignmentProcess;
  houseConsignment: IE3ConsignmentHouseLevel;
  equipment: IE3TransportEquipment;
  data: EquipmentConsignmentDetailsComponentData;
  isNewEquipment: boolean;
  editMode: boolean;
  containerProcess: IE3ContainerProcess;

  goodsItems: GoodsItemSummaryWithHouseConsignments[];
  placement: IE3GoodsPlacement = {};
  private selectedTab: TabInfo;
  visit: Visit;

  sealIdentifiersFormatter = (values: string[]) => values?.join(" | ") || null;

  protected emptyIndicatorFormatter = (v: boolean) => v ? 'Empty' : 'Not empty';

  detailsPredicate: (e: ErrorPointer) => boolean = (p => p.type === "equipment" && p.model === this.equipment);

  ngOnInit(): void {
    if (this.data.cachedConsignmentProcess) {
      this.setConsignment(this.data.cachedConsignmentProcess, true);
    } else {
      sendQuery("com.portbase.bezoekschip.common.api.consignments.queries.GetConsignment", <IE3GetConsignment>{
        consignmentProcessId: this.data.consignmentProcessId
      }, {caching: false}).subscribe((c: IE3ConsignmentProcess) => this.setConsignment(c, true));
    }
  }

  onTabChange(selectedTab: TabInfo) {
    this.selectedTab = selectedTab;
  }

  remove = () => {
    openConfirmationModalWithCallback((confirmed) => {
      if (confirmed) {
        ConsignmentUtils.allGoodsItems(this.consignmentProcess).forEach(g => {
          g.goodsPlacements = g.goodsPlacements.filter(p => p.containerIdentificationNumber !== this.equipment.containerIdentificationNumber);
        });
        delete this.consignmentProcess.consignmentMasterLevel.transportEquipmentMap[this.equipment.containerIdentificationNumber];
        this.elementRef.nativeElement.dispatchEvent(new Event("change", {bubbles: true, cancelable: true}));
        this.openConsignment();
      }
    }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
      type: "danger",
      title: "Remove equipment",
      message: this.houseConsignment
        ? `You are about to remove equipment: ${this.equipment.containerIdentificationNumber} from house B/L ${this.houseConsignment.consignmentNumber}`
        : `You are about to remove equipment: ${this.equipment.containerIdentificationNumber} from master B/L ${this.consignmentProcess.consignmentMasterLevel.consignmentNumber}`,
      question: "Are you sure that you want to remove this equipment?",
      confirmText: "Yes",
      cancelText: "No"
    }, 'static');
  }

  toggleEdit = () => {
    this.editMode = !this.editMode && !this.consignmentProcess.fromVisit;
  }

  saveOrDeclare = () => {
    if (this.isValid && this.processModel(this.hasBeenDeclared)) {
      if (this.hasBeenDeclared) {
        if (this.allowedToDeclare) {
          if (checkValidity(this.elementRef)) {
            ConsignmentUtils.declareConsignment(this.consignmentProcess, this.visit);
          }
        } else {
          this.toggleEdit();
        }
      } else {
        sendCommand("com.portbase.bezoekschip.common.api.consignments.commands.SaveConsignment", <IE3SaveConsignment>{
          consignmentProcessId: this.consignmentProcess.consignmentProcessId,
          consignment: this.consignmentProcess.consignmentMasterLevel,
          filing: this.consignmentProcess.filing
        }, () => {
          formDataSaved();
          this.toggleEdit();
        });
      }
    }
  }

  validateContainerNumber = (): string => {
    if (!this.equipment.containerIdentificationNumber) {
      return "Container identification number is required";
    }
    if (!this.equipment?.containerSizeAndType?.code || ["99U7", "99U8", "99U9"].includes(this.equipment.containerSizeAndType.code)) {
      return null;
    }
    if (ConsignmentRules.containerRegex.test(this.equipment.containerIdentificationNumber)) {
      return null;
    }
    return "Container identification number not in correct format (for example ABCD1234567)";
  };

  get allowedToDeclare(): boolean {
    return ConsignmentRules.isAllowedToDeclare(this.consignmentProcess);
  }

  get hasBeenDeclared(): boolean {
    return ConsignmentUtils.hasBeenDeclared(this.consignmentProcess);
  }

  private openConsignment() {
    if (this.houseConsignment) {
      this.openHouseConsignment();
    } else {
      this.openMasterConsignment();
    }
  }

  processModelAndOpenMasterConsignment = () => {
    this.processModel(false);
    this.openMasterConsignment();
  }

  private openMasterConsignment = () => {
    publishHandlerEvent('cachedConsignmentUpdated', this.consignmentProcess);
    openEditModal(MasterConsignmentDetailsComponent, <MasterConsignmentDetailsComponentData>{
      cachedConsignmentProcess: this.consignmentProcess
    }, {
      warnOnClose: true,
      currentToStack: true
    });
  }

  processModelAndOpenHouseConsignment = () => {
    this.processModel(false);
    this.openHouseConsignment();
  }

  private openHouseConsignment = () => {
    publishHandlerEvent('cachedConsignmentUpdated', this.consignmentProcess);
    openEditModal(HouseConsignmentDetailsComponent, <HouseConsignmentDetailsComponentData>{
      consignmentProcessId: this.consignmentProcess.consignmentProcessId,
      consignmentNumber: this.houseConsignment.consignmentNumber,
      cachedConsignmentProcess: this.consignmentProcess
    }, {
      warnOnClose: true,
      currentToStack: true
    });
  }

  get isEditable() {
    return ConsignmentUtils.isEditable(this.consignmentProcess);
  }

  get isEditableForTab() {
    return this.isEditable && this.selectedTab?.index <= 0;
  }

  get oversize() {
    return this.equipment.oversize != null;
  }

  set oversize(oversize: boolean) {
    this.equipment.oversize = oversize ? {} : null;
  }

  get isValid(): boolean {
    return checkValidity(this.elementRef);
  }

  get terminalDataOversize(): boolean {
    return this.containerProcess?.terminalData?.oversize != null && !!Object.values(this.containerProcess?.terminalData?.oversize).filter(a => a).length;
  }

  compareSizeTypeByCode = (value: IE3SizeType, compare: IE3SizeType) => value?.code !== compare?.code;

  processModel = (registerErrors: boolean) => ConsignmentUtils.validateEquipment(
    this.consignmentProcess, this.equipment, registerErrors, this.isNewEquipment, this.placement,
    this.visit, this.houseConsignment, this.data.goodsItem);

  private createEquipment = (): IE3TransportEquipment => {
    return {
      sealIdentifiers: [],
      empty: false
    };
  }

  private setConsignment = (c: IE3ConsignmentProcess, updateEditMode?: boolean) => {
    this.consignmentProcess = c;
    if (updateEditMode && !ConsignmentUtils.hasBeenDeclared(c) && this.isEditable) {
      this.editMode = true;
    }
    this.houseConsignment = c.consignmentMasterLevel.consignmentsHouseLevel
      .find(c => c.consignmentNumber === this.data.houseConsignmentNumber);
    this.equipment = c.consignmentMasterLevel.transportEquipmentMap[this.data.equipmentNumber];
    if (!this.equipment) {
      this.isNewEquipment = true;
      this.editMode = true;
      this.equipment = this.createEquipment();
    } else {
      if (this.consignmentProcess.filing.crn) {
        sendQuery("com.portbase.bezoekschip.common.api.consignments.containers.queries.GetContainerProcess", <IE3GetContainerProcess>{
          containerProcessId: ContainerUtils.constructContainerProcessId(this.consignmentProcess.filing.crn, this.equipment.containerIdentificationNumber)
        }, {caching: false}).subscribe((c: IE3ContainerProcess) => this.containerProcess = c);
        sendQuery("com.portbase.bezoekschip.common.api.visit.GetVisit", <GetVisit>{crn: this.consignmentProcess.filing.crn})
          .subscribe(v => this.visit = v);
      }
    }
    const goodsItems = (this.houseConsignment
      ? this.houseConsignment.goodsItems
      : ConsignmentUtils.allGoodsItems(this.consignmentProcess)).filter(g => g.goodsPlacements.find(
      p => p.containerIdentificationNumber === this.equipment.containerIdentificationNumber));
    this.goodsItems = ConsignmentUtils.toConsignmentGoodsItemsSummaries(goodsItems)
      .map(g => (<GoodsItemSummaryWithHouseConsignments>{
        goodsItem: g,
        houseConsignments: this.houseConsignment ? [] : this.consignmentProcess.consignmentMasterLevel.consignmentsHouseLevel
          .filter(h => h.goodsItems.some(
            hg => hg.commodity?.commodityCode?.harmonizedSystemSubHeadingCode === g.commodity?.commodityCode?.harmonizedSystemSubHeadingCode
              && hg.goodsPlacements.some(gp => gp.containerIdentificationNumber === this.equipment.containerIdentificationNumber))
          )
          .map(h => h.consignmentNumber),
        clearance: ConsignmentUtils.getClearanceOfGoodsItem(this.consignmentProcess.status, g.goodsItemNumber)
      }));
  }

  get isDutchPort() {
    return ConsignmentRules.isDutchPort(this.consignmentProcess);
  }

  get isShortLanded() {
    return ConsignmentRules.isShortLanded(this.consignmentProcess, this.equipment.containerIdentificationNumber);
  }

  @HandleQuery("getConsignment")
  getConsignment(): Observable<IE3ConsignmentProcess> {
    return of(this.consignmentProcess);
  }

  @HandleQuery("getConsignmentErrors")
  getConsignmentErrors(): Observable<ErrorWithPointers[]> {
    return this.sendQuery("getParsedConsignmentErrors", <GetErrorPointers>{
      consignmentProcess: this.consignmentProcess,
      taskStatus: this.consignmentProcess.ensTaskStatus,
      status: this.consignmentProcess.status
    });
  }

  @HandleQuery("getErrors")
  getErrors(): Observable<ErrorWithPointers[]> {
    return this.getConsignmentErrors()
      .pipe(map((e: ErrorWithPointers[]) => {
        const predicate = p => p.model === this.equipment;
        return e.filter(p => p.pointers.some(predicate))
          .map(e => ({
            ...e,
            pointers: lodash.drop(e.pointers, lodash.findIndex(e.pointers, predicate))
          }));
      }));
  }
}

export interface EquipmentConsignmentDetailsComponentData {
  consignmentProcessId: string;
  equipmentNumber: string;
  houseConsignmentNumber: string;
  goodsItem: IE3GoodsItem;
  cachedConsignmentProcess: IE3ConsignmentProcess;
}
